// Föreningsstyrd ("club-controlled") payment fees — always on. When a member
// pays an invoice, the club receives the invoice total minus Stripe's
// processing fee minus our fixed platform fee. This module is the single place
// the SPA estimates that net payout for display.
//
// The figures are platform-wide constants. The Stripe portion is an ESTIMATE —
// the real processing fee is only known after the charge (it varies by card) —
// so any payout figure shown to the club must be labelled "ca." / "estimated".
//
// The platform fee IS collected for real: the backend sets application_fee_amount
// on the Stripe Checkout charge (internal/services/billing — PlatformFeeMinor),
// so it routes to SportAdmin's platform account and the club's payout is reduced
// accordingly. The Stripe-fee portion here stays an estimate (the exact fee is
// only known after the charge), which is why payout figures are labelled "ca.".
// Keep these policies in sync with internal/services/billing/fees.go
// (OneTimeFeePolicy / RecurringFeePolicy).

// All values in minor units (öre/cents) of the invoice currency.
export const STRIPE_FEE_BPS = 150; // ≈1.5% — Swedish/EEA card estimate
export const STRIPE_FEE_FIXED_MINOR = 180; // 1.80 per transaction

// A flat fee alone is regressive — 29.99 is 0.75% of a 4 000 kr invoice but 15%
// of a 200 kr one — so each rate is bounded on both sides: never more than
// maxBps of the payment, never more than flatMinor, never less than floorMinor.
// Mirrors billing.FeePolicy.
export interface FeePolicy {
  flatMinor: number;
  maxBps: number;
  floorMinor: number;
}

// One-time: an annual/season invoice, equipment, a one-off. 29.99 headline.
export const ONE_TIME_FEE_POLICY: FeePolicy = {
  flatMinor: 2999,
  maxBps: 200,
  floorMinor: 199,
};

// Recurring: one subscription period. 4.99 headline — twelve small charges a
// year rather than one large one, so the one-time rate would take 12× as much.
export const RECURRING_FEE_POLICY: FeePolicy = {
  flatMinor: 499,
  maxBps: 200,
  floorMinor: 199,
};

// Headline one-time fee, for surfaces that quote the rate rather than compute a
// specific fee.
export const PLATFORM_FEE_MINOR = ONE_TIME_FEE_POLICY.flatMinor;

// platformFee applies a policy to a payment amount. Mirrors FeePolicy.Fee in Go.
export function platformFee(amountMinor: number, policy: FeePolicy): number {
  if (amountMinor <= 0) return 0;
  let fee = policy.flatMinor;
  if (policy.maxBps > 0) {
    const capped = Math.floor((amountMinor * policy.maxBps) / 10000);
    if (capped < fee) fee = capped;
  }
  if (fee < policy.floorMinor) fee = policy.floorMinor;
  if (fee > amountMinor) fee = amountMinor;
  return Math.max(0, fee);
}

export interface PayoutEstimate {
  stripeFeeMinor: number;
  platformFeeMinor: number;
  netMinor: number;
}

// estimatePayout returns the estimated club payout for a paid invoice of
// `totalMinor`. Fees never push the net below zero. A zero/empty invoice has
// no fees so the breakdown stays clean while the admin is still adding lines.
//
// Pass recurring=true for one period of a subscription plan, which carries the
// lower per-charge rate.
export function estimatePayout(totalMinor: number, recurring = false): PayoutEstimate {
  if (totalMinor <= 0) {
    return { stripeFeeMinor: 0, platformFeeMinor: 0, netMinor: 0 };
  }
  const stripeFeeMinor =
    Math.round((totalMinor * STRIPE_FEE_BPS) / 10000) + STRIPE_FEE_FIXED_MINOR;
  const platformFeeMinor = platformFee(
    totalMinor,
    recurring ? RECURRING_FEE_POLICY : ONE_TIME_FEE_POLICY,
  );
  const netMinor = Math.max(0, totalMinor - stripeFeeMinor - platformFeeMinor);
  return { stripeFeeMinor, platformFeeMinor, netMinor };
}
